// ============================================================================
// Study view: a free-practice setup screen (game + scope + skill) and a session
// runner that iterates items, renders the chosen game, and writes each result
// through record_review (spec §7.1–7.3). Guided-path sequencing is a later step;
// this covers free practice + due review across all seven games.
// ============================================================================

import { el, mount, toast } from '../dom.js';
import * as api from '@core/data/api.js';
import { SKILL, ITEM_TYPE, SESSION_MODE } from '@core/models/constants.js';
import { GAMES, gameById } from '../games/index.js';

const SCOPES = [
  { id: 'due', label: 'Due review (mixed)' },
  { id: 'characters', label: 'Characters' },
  { id: 'words', label: 'Words' },
];

export function renderStudy({ navigate }) {
  const container = el('div.container.stack');
  const data = { chars: new Map(), words: new Map(), sentencesByWord: new Map(), loaded: false };
  // #/study/deck/<id> pre-selects that deck as the practice scope.
  const routeParts = (location.hash || '').split('/');
  const initialDeckScope = routeParts[2] === 'deck' && routeParts[3] ? `deck:${routeParts[3]}` : null;
  let decks = [];

  async function ensureData() {
    if (data.loaded) return;
    const [c, w, s] = await Promise.all([
      api.listCharacters({ limit: 2000 }),
      api.listWords({ limit: 1000 }),
      api.listSentences().catch(() => []),
    ]);
    data.chars = new Map(c.map((r) => [r.id, r]));
    data.words = new Map(w.map((r) => [r.id, r]));
    data.charList = c;
    data.wordList = w;
    data.sentencesByWord = new Map();
    for (const sent of s) {
      if (sent.word_id && !data.sentencesByWord.has(sent.word_id)) {
        data.sentencesByWord.set(sent.word_id, sent);
      }
    }
    data.loaded = true;
  }

  // --- Setup screen ---------------------------------------------------------
  async function setup() {
    mount(container, el('div.card', { text: 'Loading…' }));
    if (!decks.length) decks = await api.myDecks().catch(() => []);
    let gameId = GAMES[0].id;
    let scopeId = initialDeckScope && decks.some((d) => `deck:${d.id}` === initialDeckScope) ? initialDeckScope : 'due';
    let skill = gameById(gameId).skill;
    const deckScope = (d) => ({ id: `deck:${d.id}`, label: `${d.kind === 'favorite' ? '★ ' : '📚 '}${d.name} (${d.item_count})` });

    const gameSel = el('select.input', {},
      GAMES.map((g) => el('option', { value: g.id, text: g.label })));
    const scopeSel = el('select.input');
    const skillSel = el('select.input', {}, [
      el('option', { value: SKILL.RECOGNITION, text: 'Recognition' }),
      el('option', { value: SKILL.PRODUCTION, text: 'Production' }),
    ]);

    function syncScopes() {
      const g = gameById(gameId);
      const valid = SCOPES.filter((s) =>
        s.id === 'due' ||
        (s.id === 'characters' && g.appliesChar) ||
        (s.id === 'words' && g.appliesWord))
        .concat(decks.filter((d) => d.item_count > 0).map(deckScope));
      scopeSel.replaceChildren(...valid.map((s) => el('option', { value: s.id, text: s.label })));
      if (!valid.find((s) => s.id === scopeId)) scopeId = valid[0].id;
      scopeSel.value = scopeId;
      skill = g.skill; skillSel.value = skill;
    }
    gameSel.onchange = () => { gameId = gameSel.value; syncScopes(); };
    scopeSel.onchange = () => { scopeId = scopeSel.value; };
    skillSel.onchange = () => { skill = skillSel.value; };
    syncScopes();

    mount(container, el('div.card.stack', {}, [
      el('h1', { text: 'Practice' }),
      el('div.field', {}, [el('label', { text: 'Game' }), gameSel]),
      el('div.field', {}, [el('label', { text: 'Scope' }), scopeSel]),
      el('div.field', {}, [el('label', { text: 'Skill direction' }), skillSel]),
      el('div.row', {}, [
        el('button.btn.btn--primary', { text: 'Start session →', onclick: () => start(gameId, scopeId, skill) }),
        el('button.btn.btn--ghost', { text: '← Dashboard', onclick: () => navigate('#/') }),
      ]),
    ]));
  }

  // --- Build the item queue for a scope -------------------------------------
  async function buildQueue(game, scopeId, skill) {
    if (scopeId === 'due') {
      const q = await api.dueQueue(20);
      const items = (Array.isArray(q) ? q : []).map((it) => ({ ...it, skill: it.skill || skill }));
      return items.filter((it) =>
        (it.item_type === ITEM_TYPE.CHARACTER && game.appliesChar) ||
        (it.item_type === ITEM_TYPE.WORD && game.appliesWord));
    }
    if (scopeId.startsWith('deck:')) {
      const contents = await api.deckContents(scopeId.slice(5));
      return (Array.isArray(contents) ? contents : [])
        .map((it) => ({ item_type: it.item_type, item_id: it.item_id, skill, mastery_state: 'new' }))
        .filter((it) =>
          (it.item_type === ITEM_TYPE.CHARACTER && game.appliesChar) ||
          (it.item_type === ITEM_TYPE.WORD && game.appliesWord));
    }
    // Free-practice character/word scopes stay in the CORE set — advanced/rare
    // content is only reached via the Advanced Browse tab or an explicit deck.
    const isChars = scopeId === 'characters';
    const rows = (isChars ? data.charList : data.wordList)
      .filter((r) => (isChars ? !r.is_rare : !r.is_advanced));
    const type = isChars ? ITEM_TYPE.CHARACTER : ITEM_TYPE.WORD;
    return rows.slice(0, 20).map((r) => ({ item_type: type, item_id: r.id, skill, mastery_state: 'new' }));
  }

  // --- Session runner -------------------------------------------------------
  async function start(gameId, scopeId, skill) {
    const game = gameById(gameId);
    mount(container, el('div.card', { text: 'Loading…' }));
    try {
      await ensureData();
      const queue = await buildQueue(game, scopeId, skill);
      if (!queue.length) {
        mount(container, doneCard('Nothing to practice in this scope right now.', { retry: setup }));
        return;
      }
      run(game, queue, 0, { reviewed: 0, xp: 0 });
    } catch (e) {
      mount(container, doneCard(e.message || 'Could not start session', { retry: setup }));
    }
  }

  function contentFor(item) {
    return item.item_type === ITEM_TYPE.CHARACTER ? data.chars.get(item.item_id) : data.words.get(item.item_id);
  }
  function poolFor() {
    return { chars: data.charList || [], words: data.wordList || [] };
  }

  function run(game, queue, idx, tally) {
    if (idx >= queue.length) {
      mount(container, doneCard(`Session complete — ${tally.reviewed} reviewed, +${tally.xp} XP 🎉`, { retry: setup }));
      // Award any newly-earned badges from this session's progress.
      api.checkBadges().then((newly) => {
        if (newly && newly.length) toast(`New badge${newly.length > 1 ? 's' : ''} earned! See your dashboard.`, 'success');
      }).catch(() => {});
      return;
    }
    const item = queue[idx];
    const content = contentFor(item);
    if (!content) return run(game, queue, idx + 1, tally);
    const sentence = item.item_type === ITEM_TYPE.WORD ? data.sentencesByWord.get(item.item_id) : null;

    async function onGrade({ grade, isCorrect, responseMs = null, skip = false }) {
      if (!skip) {
        try {
          const res = await api.recordReview({
            itemType: item.item_type, itemId: item.item_id,
            skill: item.skill || game.skill, gameType: game.id,
            grade, isCorrect, responseMs,
          });
          tally.reviewed += 1;
          if (res?.points_earned) { tally.xp += res.points_earned; toast(`+${res.points_earned} XP`, 'success'); }
        } catch (e) {
          toast(e.message || 'Could not save review', 'error');
          if (/activated/i.test(e.message || '')) return navigate('#/');
        }
      }
      run(game, queue, idx + 1, tally);
    }

    const body = game.build({ item, content, pool: poolFor(), sentence, onGrade });
    mount(container, el('div.card.stack', {}, [
      el('div.row', {}, [
        el('span.faint', { text: `${idx + 1} / ${queue.length}` }),
        el('div.appbar__spacer'),
        el('span.faint', { text: game.label }),
        el('span.faint', { text: item.skill || game.skill }),
      ]),
      body,
    ]));
  }

  function doneCard(msg, { retry }) {
    return el('div.card.stack.center', {}, [
      el('h2', { text: 'Done' }),
      el('p.muted', { text: msg }),
      el('div.row.center', {}, [
        el('button.btn.btn--primary', { text: 'New session', onclick: retry }),
        el('button.btn', { text: '← Dashboard', onclick: () => navigate('#/') }),
      ]),
    ]);
  }

  setup();
  return container;
}
